import React, { useState, useEffect } from 'react';
import Network from '../../../models/Network.js';
import UserListElement from '../list_elements/UserListElement.js';
import './Module.css';
import LoadingListElement from '../list_elements/LoadingListElement.js';
const key = process.env.REACT_APP_CLIENT_ACCESS_KEY;
const URLS = require("../../../urls.js");
const StatusCodes = require('../../../status-codes/status-codes.js');
const ErrorCodes = require('../../../error-codes/error-codes.js');

const UserListModule = ({ user, isSmallScreen }) => {
    const [usersData, setUsersData] = useState(null);
    const [agenciesData, setAgenciesData] = useState(null);
    const [userAgencies, setUserAgencies] = useState({});

    const [newName, setNewName] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newAgencyName, setNewAgencyName] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [createMessage, setCreateMessage] = useState('');

    const getAllUsers = async () => {
        const url = URLS.GET_ALL_USERS;
        const dict = {
            uid: user.uid,
            CLIENT_ACCESS_KEY: key
        };

        let responseData;

        await Network.post(url, dict, (response) => {
            responseData = response.data;
            const status = response.status;

            if (Network.checkValidStatusAndResponse(response)) {
                // success case
            } else if (status === StatusCodes.INTERNAL_SERVER_ERROR) {
                console.log("Server Error");
            } else {
                console.log("Unknown Error");
            }
        });

        return responseData;
    };

    const getAllAgencies = async () => {
        const url = URLS.GET_ALL_AGENCIES;
        const dict = {
            uid: user.uid,
            CLIENT_ACCESS_KEY: key
        };

        let responseData;

        await Network.post(url, dict, (response) => {
            responseData = response.data;
            const status = response.status;

            if (Network.checkValidStatusAndResponse(response)) {
                // success case
            } else if (status === StatusCodes.INTERNAL_SERVER_ERROR) {
                console.log("Server Error");
            } else {
                console.log("Unknown Error");
            }
        });

        return responseData;
    };

    const getUserAgencies = async (targetUid) => {
        const url = URLS.GET_USER_AGENCIES_URL;
        const dict = {
            uid: targetUid,
            CLIENT_ACCESS_KEY: key
        };

        let responseData;

        await Network.post(url, dict, (response) => {
            responseData = response.data;
            const status = response.status;

            if (Network.checkValidStatusAndResponse(response)) {
                // success case
            } else if (status === StatusCodes.INTERNAL_SERVER_ERROR) {
                console.log("Server Error");
            } else {
                console.log("Unknown Error");
            }
        });

        return responseData;
    };

    const refreshUserAgencies = async (targetUid) => {
        const agencies = await getUserAgencies(targetUid);
        setUserAgencies(prev => ({
            ...prev,
            [targetUid]: agencies
        }));
    };

    const createUser = async () => {
        if (!newName.trim() || !newEmail.trim() || !newPassword.trim()) {
            setCreateMessage('Please fill in name, email, and password');
            return;
        }

        setIsCreating(true);
        setCreateMessage('');

        const url = URLS.CREATE_USER_URL;
        const dict = {
            email: newEmail.trim(),
            agency_name: newAgencyName.trim(),
            password: newPassword,
            name_of_registrant: newName.trim(),
            CLIENT_ACCESS_KEY: key
        };

        try {
            await Network.post(url, dict, (response) => {
                const status = response.status;
                const responseData = response.data;

                if (Network.checkValidStatusAndResponse(response)) {
                    setCreateMessage('User created successfully! Expand their row below to link them to an agency.');
                    setNewName('');
                    setNewEmail('');
                    setNewPassword('');
                    setNewAgencyName('');

                    getAllUsers()
                        .then((data) => {
                            setUsersData(data);
                        });
                } else if (status === StatusCodes.INTERNAL_SERVER_ERROR) {
                    setCreateMessage('Server Error - Please check the email format and try again');
                } else if (responseData && responseData.toString() === ErrorCodes.EMAIL_IN_USE) {
                    setCreateMessage('That email is already in use');
                } else {
                    setCreateMessage('Error creating user');
                }
            });
        } catch (error) {
            setCreateMessage('Error creating user');
            console.log('Error:', error);
        } finally {
            setIsCreating(false);
        }
    };

    const handleUserExpand = async (userUid) => {
        if (!userAgencies[userUid]) {
            try {
                await refreshUserAgencies(userUid);
            } catch (error) {
                console.log('Error fetching user agencies:', error);
            }
        }
    };

    const handleLinkAgency = async (userUid, agencyUid) => {
        const url = URLS.LINK_USER_TO_AGENCY;
        const dict = {
            uid: user.uid,
            user_id: parseInt(userUid),
            agency_id: parseInt(agencyUid),
            CLIENT_ACCESS_KEY: key
        };

        await Network.post(url, dict, async (response) => {
            if (Network.checkValidStatusAndResponse(response)) {
                await refreshUserAgencies(userUid);
                getAllUsers().then((data) => setUsersData(data));
            }
        });
    };

    const handleUnlinkAgency = async (userUid, agencyUid) => {
        const url = URLS.UNLINK_USER_FROM_AGENCY;
        const dict = {
            uid: user.uid,
            user_id: parseInt(userUid),
            agency_id: parseInt(agencyUid),
            CLIENT_ACCESS_KEY: key
        };

        await Network.post(url, dict, async (response) => {
            if (Network.checkValidStatusAndResponse(response)) {
                await refreshUserAgencies(userUid);
                getAllUsers().then((data) => setUsersData(data));
            }
        });
    };

    const handleDeleteUser = async (userUid) => {
        const url = URLS.DELETE_USER;
        const dict = {
            uid: user.uid,
            user_id: parseInt(userUid),
            CLIENT_ACCESS_KEY: key
        };

        await Network.post(url, dict, async (response) => {
            if (Network.checkValidStatusAndResponse(response)) {
                setUserAgencies(prev => {
                    const next = { ...prev };
                    delete next[userUid];
                    return next;
                });
                getAllUsers().then((data) => setUsersData(data));
            }
        });
    };

    useEffect(() => {
        getAllUsers()
            .then((data) => {
                setUsersData(data);
            })
            .catch((error) => {
                console.log('Error:', error);
            });

        getAllAgencies()
            .then((data) => {
                setAgenciesData(data);
            })
            .catch((error) => {
                console.log('Error:', error);
            });
    }, [user.uid]);

    const sortUsers = (users) => {
        return [...users].sort((a, b) => {
            const nameA = a.account_owner_name || '';
            const nameB = b.account_owner_name || '';
            const nameCompare = nameA.localeCompare(nameB);
            if (nameCompare !== 0) return nameCompare;
            return a.uid - b.uid;
        });
    };

    const createUserListElement = (userRow) => {
        return (
            <UserListElement
                key={userRow.uid}
                user={userRow}
                linkedAgencies={userAgencies[userRow.uid] || []}
                availableAgencies={agenciesData || []}
                onExpand={() => handleUserExpand(userRow.uid)}
                onLink={(agencyUid) => handleLinkAgency(userRow.uid, agencyUid)}
                onUnlink={(agencyUid) => handleUnlinkAgency(userRow.uid, agencyUid)}
                onDelete={() => handleDeleteUser(userRow.uid)}
            />
        );
    };

    return (
        <div className={isSmallScreen ? "standard-module-screen-small" : "standard-module"}>
            {/* Create User Interface */}
            <div className="user-association-section">
                <h3>Create User</h3>
                <div className="user-association-form">
                    <div className="form-group">
                        <label htmlFor="newName">Full Name:</label>
                        <input
                            type="text"
                            id="newName"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            placeholder="Enter full name"
                            className="standard-input"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="newEmail">Email:</label>
                        <input
                            type="email"
                            id="newEmail"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            placeholder="Enter email"
                            className="standard-input"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="newPassword">Password:</label>
                        <input
                            type="password"
                            id="newPassword"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Enter password"
                            className="standard-input"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="newAgencyName">Agency Name (optional):</label>
                        <input
                            type="text"
                            id="newAgencyName"
                            value={newAgencyName}
                            onChange={(e) => setNewAgencyName(e.target.value)}
                            placeholder="Optional display name"
                            className="standard-input"
                        />
                    </div>
                    <button
                        onClick={createUser}
                        disabled={isCreating}
                        className="link-button"
                    >
                        {isCreating ? 'Creating...' : 'Create User'}
                    </button>
                </div>
                {createMessage && (
                    <div className={`link-message ${createMessage.includes('successfully') ? 'success' : 'error'}`}>
                        {createMessage}
                    </div>
                )}
            </div>

            {/* Users List */}
            {usersData ? (
                sortUsers(usersData).map((userRow) =>
                    createUserListElement(userRow)
                )
            ) : (
                <LoadingListElement />
            )}
        </div>
    );
};

export default UserListModule;
